import { bound } from "@frui.ts/helpers";
import { getDay } from "date-fns";
import { Router, ScreenBase, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import debounce from "debounce-promise";
import { distanceTo, insideCircle, Location } from "geolocation-utils";
import take from "lodash/take";
import { action, computed, observable, reaction, runInAction } from "mobx";
import LocalizationService from "services/localizationService";
import OpeningHoursResponse from "entities/openingHoursResponse";
import TradingPlaceService from "services/tradingPlaceService";
import Free2023 from "assets/geodata/2023/free.json";
import Paid2023 from "assets/geodata/2023/paid.json";
import Partial2023 from "assets/geodata/2023/partial.json";
import Free2024 from "assets/geodata/2024/free.json";
import Paid2024 from "assets/geodata/2024/paid.json";
import Partial2024 from "assets/geodata/2024/partial.json";
import Free2025 from "assets/geodata/2025/free.json";
import Paid2025 from "assets/geodata/2025/paid.json";
import Partial2025 from "assets/geodata/2025/partial.json";
import TradingPlaceDetailResponse from "entities/tradingPlaceDetailResponse";
import TradingPlaceType from "entities/tradingPlaceType";
import TradingPlaceResponse from "entities/tradingPlaceResponse";
import OpeningHoursKind from "entities/openingHoursKind";
import OpeningHoursIntervalResponse from "entities/openingHoursIntervalResponse";

type OnGoogleApiLoadedProps = {
  map: google.maps.Map;
  maps?: any;
  ref?: any;
};

export const TradingPlacesModuleNavigationName = "trading_places";

export type Day = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";
export const dayNames: Day[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

type VisibleRoads = {
  free: boolean;
  paid: boolean;
  year: number;
};

type VisibleTradingPlaces = {
  kiosk: boolean;
  salesPoint: boolean;
};

type AvailableMaps = {
  [keyof: number]: {
    free: any;
    paid: any;
    partial: any;
  };
};

@Router.registerRoute({ route: TradingPlacesModuleNavigationName, name: "trading_places" })
export default class TradingPlacesViewModel extends ScreenBase {
  busyWatcher = new BusyWatcher();
  unsubscriptions: Array<() => void> = [];

  @observable place?: TradingPlaceDetailResponse;
  geocoderService: google.maps.Geocoder;
  maxResultsCount = 5;
  @observable showMap = false;
  private searchCache = new Map<string, TradingPlaceDetailResponse[] | undefined>();
  private closureDateRegexp = /%date%/g;
  private closureSinceRegexp = /%since%/g;
  private closureUntilRegexp = /%until%/g;

  /*https://gitlab.cendis.cz/edaz/edaz-tasks/-/issues/1208 - hide year 2023*/
  roadMapYears = [2025 /* 2024   2023 */];

  availableRoadMaps: AvailableMaps = {
    2025: {
      free: Free2025,
      paid: Paid2025,
      partial: Partial2025,
    },
    /*2024: {
      free: Free2024,
      paid: Paid2024,
      partial: Partial2024,
    },
      2023: {
      free: Free2023,
      paid: Paid2023,
      partial: Partial2023,
    }, */
  };

  @observable map?: google.maps.Map = undefined;
  @observable visibleRoads: VisibleRoads;
  @observable visibleTradingPlaces: VisibleTradingPlaces;
  @observable tooltipMessage = {
    road: undefined,
    roadType: undefined,
  };
  @observable showTooltip = false;
  @observable tooltipPosition = {
    x: 0,
    y: 0,
  };
  @observable searchValue = "";
  @observable suggestedPlaces: TradingPlaceResponse[] = [];
  @observable mobileFilterOpen = false;
  @observable mapButtonTitle: string;
  @observable mapButtonClassName = "";
  @observable mapButtonContainerClassName = "text-center text-sm-left";
  @observable mobileHideSalesPoint = true;

  translateGeneral = (code: string) => {
    return this.localizationService.translateGeneral(`tradingPlaces.${code}`);
  };

  constructor(public localizationService: LocalizationService, public tradingPlacesService: TradingPlaceService) {
    super();
    this.navigationName = TradingPlacesModuleNavigationName;
    this.localizationService = localizationService;
    this.nameValue = "tradingPlaces.title";
    this.mapButtonTitle = this.translateGeneral("showUpMap");
    this.visibleRoads = {
      free: false,
      paid: false,
      year: this.roadMapYears[0],
    };
    this.visibleTradingPlaces = {
      kiosk: true,
      salesPoint: true,
    };
    this.unsubscriptions.push(
      reaction(() => [this.visibleRoads.free, this.visibleRoads.paid, this.visibleRoads.year], this.handleRoadVisibilityChange)
    );
  }

  protected onDeactivate = (isClosing: boolean): Promise<any> | void => {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  };

  openingTimeRange = (day?: OpeningHoursIntervalResponse) => {
    return day ? `${day.from}-${day.to}` : undefined;
  };

  holidayDay = (openingHour: OpeningHoursResponse) => {
    if (!openingHour.week) {
      return undefined;
    }
    if (!openingHour.validSince) {
      return undefined;
    }
    return dayNames[getDay(new Date(openingHour.validSince))];
  };

  @bound
  @watchBusy
  async onShowMapClick() {
    this.showMap = true;
    await this.tradingPlacesService.initialize();
  }

  @action.bound
  closePopover() {
    this.place = undefined;
  }

  @bound
  @watchBusy
  async choosePlace(id: string) {
    const place = await this.tradingPlacesService.itemDetail(id);
    this.place = {
      ...place,
      id,
    };
  }

  /**
   * Filter out tranding places that are not available or have no coordinates
   */
  @computed
  get places() {
    // @TODO Filter nebude potřeba až bude BE vracet co má
    return this.tradingPlacesService.tradingPlaces?.items?.filter(
      place => place.isAvailable && place.latitude && place.longitude
    );
  }

  /**
   * Transform trading places data to coordinates for map
   */
  @computed
  get placesCoords() {
    if (!this.visibleTradingPlaces) {
      return [];
    }
    return this.places
      ?.filter(place => {
        if (this.visibleTradingPlaces.salesPoint && place.type === TradingPlaceType.Salespoint) {
          return true;
        }
        if (this.visibleTradingPlaces.kiosk && place.type === TradingPlaceType.Kiosk) {
          return true;
        }
        return false;
      })
      .map(place => ({
        id: place.id,
        coords: { lat: place.latitude, lng: place.longitude },
        type: place.type === TradingPlaceType.Kiosk ? ("kiosk" as const) : ("salesPoint" as const),
        open: true, // @TODO BE to teď nevrací
      }));
  }

  @computed
  get closures() {
    return (
      this.place?.salespoint?.openingHours.filter(
        item => item.kind === OpeningHoursKind.Closure && item.validSince && item.validUntil
      ) || []
    );
  }

  closureInfo = (closure: OpeningHoursResponse) => {
    if (!closure.validSince || !closure.validUntil) {
      return;
    }
    const since = this.localizationService.formatDate(new Date(closure.validSince));
    const until = this.localizationService.formatDate(new Date(closure.validUntil));

    const oneDayRelation = since === until;

    const result = oneDayRelation
      ? this.translateGeneral("closureTimeForOneDay").replace(this.closureDateRegexp, since)
      : this.translateGeneral("closureTime").replace(this.closureSinceRegexp, since).replace(this.closureUntilRegexp, until);

    return result;
  };

  /**
   * Callback for Google Maps API when it is loaded
   * @param map
   * @param maps
   * @param ref
   */
  onGoogleApiLoaded = ({ map, maps, ref }: OnGoogleApiLoadedProps) => {
    this.map = map;
    this.geocoderService = new google.maps.Geocoder();

    // Load visible roads
    this.loadRoadsForYear(this.visibleRoads.year);
    this.setVisibleRoads();

    // Add event listeners for road tooltips
    map.data.addListener("mouseover", (event: any) => {
      this.tooltipMessage = {
        road: event.feature.getProperty("Komunikace"),
        roadType: event.feature.getProperty("EDAZ"),
      };
      this.tooltipPosition = {
        x: event.domEvent.x + 10,
        y: event.domEvent.y + 10,
      };
      this.showTooltip = true;
    });
    map.data.addListener("mouseout", () => {
      this.tooltipPosition = {
        x: 0,
        y: 0,
      };
      this.tooltipMessage = {
        road: undefined,
        roadType: undefined,
      };
      this.showTooltip = false;
    });
  };

  /**
   * Handle change in road visibility state and load appropriate data
   */
  @action handleRoadVisibilityChange = () => {
    this.loadRoadsForYear(this.visibleRoads.year);
    this.setVisibleRoads();
  };

  /**
   * All trading places visible checkbox state
   */
  @computed get allTradingPlacesVisible() {
    let allValue = true;
    Object.keys(this.visibleTradingPlaces).forEach(key => {
      if (!this.visibleTradingPlaces[key as keyof VisibleTradingPlaces]) {
        allValue = false;
      }
    });
    return allValue;
  }

  set allTradingPlacesVisible(value) {
    Object.keys(this.visibleTradingPlaces).forEach(key => {
      this.visibleTradingPlaces[key as keyof VisibleTradingPlaces] = value;
    });
  }

  /**
   * Show/hide mobile filtering options
   */
  @action toggleMobileFilter = () => {
    this.mobileFilterOpen = !this.mobileFilterOpen;
  };

  /**
   * Load roads for selected year
   * @param year
   */
  @action loadRoadsForYear = (year: keyof AvailableMaps) => {
    if (this.map) {
      // Reset map data
      this.map.data.forEach(feature => this.map?.data.remove(feature));
      // Add road layer data
      this.map.data.addGeoJson(this.availableRoadMaps[year].free);
      this.map.data.addGeoJson(this.availableRoadMaps[year].paid);
      this.map.data.addGeoJson(this.availableRoadMaps[year].partial);
    }
  };

  /**
   * Set visibility of roads
   */
  @action setVisibleRoads = () => {
    if (this.map) {
      // Add styles for roads
      this.map.data.setStyle(feature => {
        const roadType = feature.getProperty("EDAZ");
        let style;
        const strokeWeight = 4;

        switch (roadType) {
          // Paid roads
          case "a":
            style = {
              strokeColor: "#C1103C",
              strokeWeight,
              visible: this.visibleRoads.paid,
            };
            break;
          // Free roads
          case "n":
            style = {
              strokeColor: "#052856",
              strokeWeight,
              visible: this.visibleRoads.free,
            };
            break;
          // Paid only in direction out of the country
          case "p":
          default:
            style = {
              strokeColor: "#b062f8",
              strokeWeight,
              visible: this.visibleRoads.free || this.visibleRoads.paid,
            };
            break;
        }
        return style;
      });
    }
  };

  @action
  toogleMobileSalesPoint() {
    this.mobileHideSalesPoint = !this.mobileHideSalesPoint;
  }

  @action searchTradingPlaces = async (query: string) => {
    if (query.length < 3) {
      return undefined;
    }

    if (this.searchCache.has(query)) {
      return this.searchCache.get(query);
    }

    let places: TradingPlaceResponse[] | undefined = undefined;

    try {
      await this.geocoderService.geocode(
        {
          address: query,
          region: "CZ",
          componentRestrictions: {
            country: "CZ",
          },
        },
        (results, status) => {
          if (results && status === google.maps.GeocoderStatus.OK) {
            const locations: Location[] = results
              // Filter out results without geometry
              .filter(result => result.geometry)
              // Filter out points of interest that are not establishments and countries
              .filter(result => {
                const types = result.types;
                return !(types.includes("point_of_interest") && !types.includes("establishment")) && !types.includes("country");
              })
              .map(result => ({
                lat: result.geometry!.location.lat(),
                lng: result.geometry!.location.lng(),
              }));

            places = this.findTradingPlacesNearToLocation(locations);

            if (places.length === 0 && locations.length !== 0) {
              places = this.findTradingPlacesNearToLocation(locations, 6000);
            }

            places = take(places, this.maxResultsCount);
          } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
            places = [];
          }

          runInAction(() => {
            this.searchCache.set(query, places);
            if (places) {
              this.suggestedPlaces = places;
            }
          });
        }
      );
    } catch (e) {
      // We don't care about Geocoder errors
    }
  };

  findTradingPlacesNearToLocation = (locations: Location[], radiusInMeters = 3000) => {
    const places: TradingPlaceResponse[] = [];
    try {
      locations.forEach(location => {
        const locationPlaces = this.places
          .filter(place => insideCircle({ lat: place.latitude, lng: place.longitude }, location, radiusInMeters))
          .sort((a, b) => {
            return (
              distanceTo({ lat: a.latitude, lng: a.longitude }, location) -
              distanceTo({ lat: b.latitude, lng: a.longitude }, location)
            );
          });

        places.push(...locationPlaces);

        if (places.length >= this.maxResultsCount) {
          throw new Error();
        }
      });
    } catch (e) {
      // Error just break foreach if we have enought result
    }

    return places;
  };

  debouncedSearchTradingPlaces = debounce(this.searchTradingPlaces, 400, { leading: true });

  tradingPlaceSelected = (tradingPlace: TradingPlaceResponse) => {
    this.choosePlace(tradingPlace.id);
  };

  translate = (code: string) => {
    return this.localizationService.translate(`general.tradingPlaces.${code}`);
  };
}
